import { getProductListQuery, getProductListSearchQuery } from './queries';

import { API } from '@/services/api';
import { type ProductListing } from '@/types/api/product';
import { type CookieStore, getClientSideCookies } from '@/utilities/cookieStore';
import { featuresFromCookies } from '@/utilities/features';
import { getOriginFromEnvironment } from '@/utilities/getOriginFromEnvironment';
import { getProductUrl } from '@/utilities/getProductUrl';
import { getUserIdAndVariationType } from '@/utilities/getUserIdAndVariationType';
import { stspFeaturesFromCookie } from '@/utilities/siteSpect';

type ProductListBody = {
  query: string;
};

type ProductListResponse = {
  data: {
    productListing?: ProductListing;
    productListingSearch?: ProductListing;
  };
  errors: { message: string }[];
};

type GetProductListProps = {
  resolvedUrl: string;
  cookies?: CookieStore;
  environment: string;
  locale: string;
};

export const getProductList = async ({
  resolvedUrl,
  cookies = getClientSideCookies(),
  environment,
  locale,
}: GetProductListProps) => {
  const { url, isSearch } = getProductUrl(resolvedUrl);
  const { userId, variationType } = getUserIdAndVariationType(cookies);
  const origin = getOriginFromEnvironment(locale, environment);

  const features = featuresFromCookies(cookies)
    .merge(stspFeaturesFromCookie(cookies))
    .ifEmpty(undefined);

  const returnObject = isSearch ? 'productListingSearch' : 'productListing';

  const response = await API.post<ProductListBody, ProductListResponse>({
    path: `${origin}/api/graphql`,
    body: {
      query: isSearch
        ? getProductListSearchQuery({ locale, userId, keywords: url })
        : getProductListQuery({ locale, url, userId, variationType, features }),
    },
  });

  // The Graphql api endpoint returns errors without the response.ok being false or the status 550
  // Thats why we need to cover the errors here because they come with the normal response 200.
  // Graphql errors explained: https://the-guild.dev/blog/graphql-error-handling-with-fp
  // Disabled for now.
  // if (response.errors) {
  //   throw new Error(response.errors.map(error => error.message).join('\n'));
  // }

  return response.data[returnObject];
};
